import { DialogTitle } from '@/components/ui/dialog';
import { CheckIcon } from '@heroicons/react/20/solid';
import { forwardRef, Fragment } from 'react';
import { cn } from '..';
import { Transition } from '@headlessui/react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export type BaseModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subTitle: string;
  isLoading?: boolean;
  children?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'full';
  showSuccess?: boolean;
  successMessage?: string;
  errorMessage?: string;
  primaryButton?: React.ReactNode;
  stickyFooter?: boolean;
  modal?: boolean;
  className?: string;
};

export const widthMap = {
  sm: 'sm:max-w-[450px]',
  md: 'sm:max-w-[550px]',
  lg: 'sm:max-w-[650px]',
  full: 'sm:max-w-[90vw]',
};

type ModalButtonProps = {
  disabled?: boolean;
  className?: string;
  buttonText: string | React.ReactNode;
};

type ModalDoneButtonProps = ModalButtonProps & {
  onClick: () => void;
};

const modalButtonClassName = `bottom-8 inline-flex w-full justify-center text-white sm:w-auto rounded-full px-8 py-2 font-medium md:text-lg`;

export function ModalDoneButton({
  disabled,
  buttonText,
  onClick,
  className,
}: ModalDoneButtonProps) {
  return (
    <div className="mt-6 flex w-full justify-end">
      <Button
        disabled={disabled}
        className={cn(modalButtonClassName, className)}
        onClick={onClick}
      >
        {buttonText}
      </Button>
    </div>
  );
}

export function ModalFormSubmitButton({ disabled, buttonText, className }: ModalButtonProps) {
  return (
    <div className="mt-6 flex w-full justify-end">
      <Button
        disabled={disabled}
        className={cn(modalButtonClassName, className)}
        type="submit"
      >
        {buttonText}
      </Button>
    </div>
  );
}

export function SuccessMessage({
  show,
  successMessage,
}: {
  show?: boolean;
  successMessage?: string;
}) {
  return (
    <Transition
      show={show}
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      enterTo="opacity-100 translate-y-0 sm:scale-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100 translate-y-0 sm:scale-100"
      leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
    >
      <div className="h-45 flex items-center justify-center bg-opacity-40">
        <div className="flex flex-col items-center justify-center rounded-lg bg-white p-10">
          <div className="flex items-center justify-center rounded-full border-8 border-green-100 bg-green-500 p-3 text-white">
            <CheckIcon
              className="h-6 w-6 "
              aria-hidden="true"
            />
          </div>
          <div className="mt-5 text-center">
            <DialogTitle className="text-xl font-semibold leading-6 text-gray-800">
              Success
            </DialogTitle>
            <p className="mt-1 text-base text-gray-700">{successMessage}</p>
          </div>
        </div>
      </div>
    </Transition>
  );
}

interface TextProps {
  text: string;
  className?: string;
}

export function FieldLabel({ text, className }: TextProps) {
  return (
    <label className={cn('text-primary-dark mb-1 ml-1 text-sm font-medium', className)}>
      {text}
    </label>
  );
}

export const TextInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => {
    return (
      <Input
        {...props}
        ref={ref}
        className={cn(
          'border-primary-300 text-primary-dark rounded-md border bg-white px-2 py-2 sm:text-sm',
          'caret-primary-dark',
          props.className,
        )}
      />
    );
  },
);

TextInput.displayName = 'TextInput';
